import { useState, useEffect } from 'react';
import { Navigate, useLocation, Link } from 'react-router-dom';
import styled from 'styled-components';
import api from '../api';
import { useAuth } from '../context/auth';
import { Card, Form, Input, Button, FormError } from '../components/FormStyle';
import { HeavyText, LightItalicText } from '../components/Style';
import { trackEvent, trackAuthError, identifyUser } from '../analytics';
import { VinylDisc, VINYL_THEMES } from '../components/UserBadge';

var CheckboxRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: #555;
  line-height: 1.4;
  cursor: pointer;
  margin-top: 0.25rem;

  input {
    margin-top: 0.15rem;
    flex-shrink: 0;
  }

  a {
    color: #0066cc;
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
  }
`;

function ChooseUsername() {
  useEffect(function () {
    document.title = 'Choose Username | HeavyRotation';
  }, []);

  var location = useLocation();
  var locationState = location.state;

  // Dev bypass: allow direct navigation to /choose-username without email flow
  if (!locationState && process.env.NODE_ENV === 'development') {
    locationState = {
      email: 'test@example.com',
      suggestedName: 'testuser',
      provider: 'google',
      idToken: 'dev',
    };
  }

  var email = locationState?.email || '';
  var suggestedName = locationState?.suggestedName || '';
  var provider = locationState?.provider || '';
  var idToken = locationState?.idToken || '';
  var referrerID = locationState?.referrerID || '';

  var authContext = useAuth();
  var setAuthTokens = authContext.setAuthTokens;

  var usernameState = useState(suggestedName.toLowerCase().replace(/[^a-z0-9_.]/g, ''));
  var username = usernameState[0];
  var setUsername = usernameState[1];

  var displayNameState = useState('');
  var displayName = displayNameState[0];
  var setDisplayName = displayNameState[1];

  var locationInputState = useState('');
  var locationInput = locationInputState[0];
  var setLocationInput = locationInputState[1];

  var errorState = useState('');
  var error = errorState[0];
  var setError = errorState[1];

  var loadingState = useState(false);
  var isLoading = loadingState[0];
  var setIsLoading = loadingState[1];

  var _acceptedTerms = useState(false);
  var acceptedTerms = _acceptedTerms[0];
  var setAcceptedTerms = _acceptedTerms[1];

  var _acceptedPrivacy = useState(false);
  var acceptedPrivacy = _acceptedPrivacy[0];
  var setAcceptedPrivacy = _acceptedPrivacy[1];

  var _confirmedAge = useState(false);
  var confirmedAge = _confirmedAge[0];
  var setConfirmedAge = _confirmedAge[1];

  var loggedInState = useState(false);
  var isLoggedIn = loggedInState[0];
  var setIsLoggedIn = loggedInState[1];

  if (!locationState) {
    return <Navigate to="/login" replace />;
  }

  if (isLoggedIn) {
    return <Navigate to="/welcome" replace />;
  }

  function handleSubmit() {
    if (!username.trim()) {
      trackAuthError('choose_username', { method: provider, code: 'username_empty' });
      setError('Please enter a username.');
      return;
    }

    if (!/^[a-z0-9_.]+$/.test(username.trim())) {
      trackAuthError('choose_username', { method: provider, code: 'username_invalid_chars' });
      setError('Username can only contain lowercase letters, numbers, periods, and underscores.');
      return;
    }

    if (!acceptedTerms || !acceptedPrivacy || !confirmedAge) {
      var unchecked = [];
      if (!acceptedTerms) unchecked.push('terms');
      if (!acceptedPrivacy) unchecked.push('privacy');
      if (!confirmedAge) unchecked.push('age');
      trackAuthError('choose_username', {
        method: provider,
        code: 'consent_not_accepted',
        detail: unchecked.join(','),
      });
      setError('Please accept all required checkboxes to continue.');
      return;
    }

    if (username.trim().length < 2) {
      trackAuthError('choose_username', { method: provider, code: 'username_too_short' });
      setError('Username must be at least 2 characters.');
      return;
    }

    setIsLoading(true);
    setError('');

    // Email-verified users: account already exists, just update username/profile
    if (provider === 'email') {
      api
        .put('/api/users/profile', {
          username: username.trim(),
          displayName: displayName.trim() || undefined,
          location: locationInput.trim() || undefined,
        })
        .then(function (result) {
          // Update auth tokens with new username
          var stored = JSON.parse(localStorage.getItem('authData') || '{}');
          if (stored.user) {
            stored.user.username = username.trim();
            if (displayName.trim()) stored.user.displayName = displayName.trim();
            if (locationInput.trim()) stored.user.location = locationInput.trim();
            setAuthTokens(stored);
          }
          localStorage.setItem('hr_has_logged_in', 'true');
          identifyUser(stored.user?.id, {
            username: username.trim(),
            $created_at: new Date().toISOString(),
          });
          trackEvent('signup_completed', { method: 'email' });
          if (referrerID) {
            api.post('/api/follows/' + referrerID).catch(function () {});
            localStorage.removeItem('signup_referrer');
          }
          setIsLoggedIn(true);
        })
        .catch(function (err) {
          var message = err?.response?.data?.reason || 'Failed to set username. Please try again.';
          trackAuthError('choose_username', { method: 'email', error: err, reason: message });
          setError(message);
          setIsLoading(false);
        });
      return;
    }

    // Social users: create account via social-signup
    var signupBody = {
      email: email,
      username: username.trim(),
      provider: provider,
      idToken: idToken,
    };
    if (referrerID) {
      signupBody.referrerID = referrerID;
    }

    api
      .post('/api/users/social-signup', signupBody)
      .then(function (result) {
        if (result.status === 200) {
          var auth = {
            user: result.data.user,
            token: result.data.token,
          };
          setAuthTokens(auth);
          localStorage.setItem('hr_has_logged_in', 'true');
          identifyUser(result.data.user.id, {
            username: result.data.user.username,
            $created_at: new Date().toISOString(),
          });
          trackEvent('signup_completed', {
            method: provider,
            signup_cta_variant: sessionStorage.getItem('signup_cta_variant') || 'control',
          });

          var promises = [];
          // Save display name and location if provided
          if (displayName.trim() || locationInput.trim()) {
            promises.push(
              api.put('/api/users/profile', {
                displayName: displayName.trim(),
                location: locationInput.trim(),
              })
            );
          }
          return Promise.all(promises).then(function () {
            // Auto-follow the referrer if one exists
            if (referrerID) {
              api.post('/api/follows/' + referrerID).catch(function () {});
              localStorage.removeItem('signup_referrer');
            }
            setIsLoggedIn(true);
            setIsLoading(false);
          });
        } else {
          trackAuthError('choose_username', {
            method: provider,
            status: result.status,
            reason: 'Signup failed. Please try again.',
          });
          setError('Signup failed. Please try again.');
        }
        setIsLoading(false);
      })
      .catch(function (e) {
        var message = e.response?.data?.reason || 'Signup failed. Please try again.';
        trackAuthError('choose_username', { method: provider, error: e, reason: message });
        setError(message);
        setIsLoading(false);
      });
  }

  return (
    <>
      <Card style={{ marginTop: '2rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <VinylDisc theme={VINYL_THEMES.test_pressing} size={48} spinning />
          <div>
            <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>
              Welcome to <HeavyText>Heavy</HeavyText>
              <LightItalicText>Rotation</LightItalicText>
            </div>
            <div style={{ fontSize: '0.85rem', color: '#666' }}>Let's get you set up.</div>
          </div>
        </div>
      </Card>
      <Card>
        <div
          style={{
            fontSize: '1.1rem',
            fontWeight: 700,
            marginBottom: '0.75rem',
            textAlign: 'center',
          }}
        >
          Choose a Username
        </div>
        <Form>
          <Input
            type="text"
            value={username}
            onChange={function (e) {
              setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_.]/g, ''));
            }}
            placeholder="username"
          />
          <Input
            type="text"
            value={displayName}
            onChange={function (e) {
              setDisplayName(e.target.value.slice(0, 50));
            }}
            placeholder="your name (optional)"
          />
          <Input
            type="text"
            value={locationInput}
            onChange={function (e) {
              setLocationInput(e.target.value.slice(0, 100));
            }}
            placeholder="location (optional)"
          />
          <div
            style={{
              background: '#f9f9f9',
              border: '1px solid #eee',
              borderRadius: '8px',
              padding: '0.75rem 1rem',
              marginTop: '1rem',
              marginBottom: '1rem',
            }}
          >
            <div
              style={{
                fontSize: '0.75rem',
                color: '#666',
                lineHeight: 1.5,
                fontStyle: 'italic',
                marginBottom: '0.6rem',
              }}
            >
              HeavyRotation is a <span style={{ fontWeight: 900 }}>supportive community</span> for
              sharing music you love. To keep things{' '}
              <span style={{ fontWeight: 900 }}>positive and safe for all</span>, we ask that you
              agree to our Terms of Service and Privacy Policy. If you've got any questions, send us
              a message at{' '}
              <a
                href="mailto:hello@heavyrotation.app"
                style={{ color: '#0066cc', textDecoration: 'none' }}
              >
                hello@heavyrotation.app
              </a>
              .
            </div>
            <CheckboxRow>
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={function () {
                  setAcceptedTerms(!acceptedTerms);
                }}
              />
              <span>
                I have read and agree to the{' '}
                <Link to="/terms" target="_blank">
                  Terms of Service &#8599;
                </Link>
              </span>
            </CheckboxRow>
            <CheckboxRow>
              <input
                type="checkbox"
                checked={acceptedPrivacy}
                onChange={function () {
                  setAcceptedPrivacy(!acceptedPrivacy);
                }}
              />
              <span>
                I have read and agree to the{' '}
                <Link to="/privacy" target="_blank">
                  Privacy Policy &#8599;
                </Link>
              </span>
            </CheckboxRow>
            <CheckboxRow>
              <input
                type="checkbox"
                checked={confirmedAge}
                onChange={function () {
                  setConfirmedAge(!confirmedAge);
                }}
              />
              <span>I confirm that I am at least 18 years of age</span>
            </CheckboxRow>
          </div>
          <Button onClick={handleSubmit} disabled={isLoading} style={{ marginTop: '0.75rem' }}>
            {isLoading ? 'Creating account...' : 'Create Account'}
          </Button>
        </Form>
        {error && <FormError>{error}</FormError>}
      </Card>
    </>
  );
}

export default ChooseUsername;
